// File: frontend/src/components/Category/CategoryTable.tsx
// Enhanced table component without modal dependencies

import React from 'react';
import { 
  FolderTree, 
  Eye, 
  EyeOff,
  Folder,
  FolderOpen,
  Hash,
  Calendar
} from 'lucide-react';
import { CategoryRow } from './CategoryRow';
import { Pagination, ActionsDropdown, createCommonActions } from '@/components/common/ui';
import { Category } from '@/lib/api/categoryService';

interface CategoryTableProps {
  categories: Category[];
  isLoading: boolean;
  searchTerm: string;
  parentFilter: string;
  statusFilter: string;
  pagination?: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from: number;
    to: number;
    has_more: boolean;
  };
  currentPage: number;
  onPageChange: (page: number) => void;
  onDelete: (categoryId: number, categoryName: string) => void;
  onToggleStatus: (categoryId: number) => void;
  onEdit: (category: Category) => void;
  onCreateCategory: () => void;
  isDeleting: boolean;
  isTogglingStatus: boolean;
}

export const CategoryTable: React.FC<CategoryTableProps> = ({
  categories,
  isLoading,
  searchTerm,
  parentFilter,
  statusFilter,
  pagination,
  currentPage,
  onPageChange,
  onDelete,
  onToggleStatus,
  onEdit,
  onCreateCategory,
  isDeleting,
  isTogglingStatus
}) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <Eye className="h-3 w-3 mr-1" />
        Active
      </span>
    ) : (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        <EyeOff className="h-3 w-3 mr-1" />
        Inactive
      </span>
    );
  };

  const getParentBadge = (category: Category) => {
    if (!category.parent_category_id) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <FolderOpen className="h-3 w-3 mr-1" />
          Parent
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        <Folder className="h-3 w-3 mr-1" />
        Sub-category
      </span>
    );
  };

  return (
    <>
      {isLoading ? (
        <div className="p-8 text-center">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading categories...</p>
        </div>
      ) : categories && categories.length > 0 ? (
        <>
          {/* Mobile/Tablet Card View */}
          <div className="block xl:hidden space-y-4">
            {categories.map((category) => (
              <div key={category.category_id} className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50">
                  {/* Category Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      {category.icon && (
                        <div className="flex-shrink-0 w-8 h-8 bg-white rounded-lg flex items-center justify-center">
                          <i className={`${category.icon} text-gray-600 text-sm`}></i>
                        </div>
                      )}
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">{category.name}</h4>
                        <p className="text-xs text-gray-500 mt-1">{category.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {getStatusBadge(category.is_active || false)}
                    </div>
                  </div>

                  {/* Category Details */}
                  <div className="grid grid-cols-2 gap-4 text-xs text-gray-500">
                    <div>
                      <span className="font-medium">Type:</span>
                      <br />
                      {getParentBadge(category)}
                    </div>
                    <div>
                      <span className="font-medium">Order:</span>
                      <br />
                      <span className="inline-flex items-center">
                        <Hash className="h-3 w-3 mr-1" />
                        {category.display_order || 0}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium">Subcategories:</span>
                      <br />
                      {category.subcategory_count || 0}
                    </div>
                    <div>
                      <span className="font-medium">Created:</span>
                      <br />
                      {category.created_at ? formatDate(category.created_at) : 'N/A'}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-end pt-3 border-t border-gray-200">
                    <ActionsDropdown
                      size="sm"
                      disabled={isDeleting || isTogglingStatus}
                      actions={[
                        createCommonActions.edit(() => onEdit(category)),
                        createCommonActions.toggleStatus(
                          category.is_active || false,
                          () => onToggleStatus(category.category_id),
                          isTogglingStatus
                        ),
                        ...((category.subcategory_count || 0) === 0 ? [
                          createCommonActions.delete(
                            () => onDelete(category.category_id, category.name),
                            isDeleting
                          )
                        ] : [])
                      ]}
                    />
                  </div>
                </div>
              ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden xl:block -mx-6 -mb-6">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Subcategories
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Order
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {categories.map((category) => (
                    <tr key={category.category_id} className="hover:bg-gray-50">
                      {/* Category */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {category.icon && (
                            <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                              <i className={`${category.icon} text-gray-600 text-sm`}></i>
                            </div>
                          )}
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {category.name}
                            </div>
                            {category.description && (
                              <div className="text-sm text-gray-500 max-w-xs truncate">
                                {category.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Type */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getParentBadge(category)}
                      </td>

                      {/* Subcategories */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900">
                          <FolderTree className="h-4 w-4 mr-1 text-gray-400" />
                          {category.subcategory_count || 0}
                        </div>
                      </td>

                      {/* Order */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900">
                          <Hash className="h-4 w-4 mr-1 text-gray-400" />
                          {category.display_order || 0}
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(category.is_active || false)}
                      </td>

                      {/* Created */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          {category.created_at ? formatDate(category.created_at) : 'N/A'}
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <ActionsDropdown
                          size="md"
                          disabled={isDeleting || isTogglingStatus}
                          actions={[
                            createCommonActions.edit(() => onEdit(category)),
                            createCommonActions.toggleStatus(
                              category.is_active || false,
                              () => onToggleStatus(category.category_id),
                              isTogglingStatus
                            ),
                            ...((category.subcategory_count || 0) === 0 ? [
                              createCommonActions.delete(
                                () => onDelete(category.category_id, category.name),
                                isDeleting
                              )
                            ] : [])
                          ]}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {pagination && pagination.last_page > 1 && (
            <div className="mt-6 -mx-6 -mb-6 px-6 py-4 border-t border-gray-200 bg-gray-50">
              <Pagination
                currentPage={pagination.current_page}
                totalPages={pagination.last_page}
                totalItems={pagination.total}
                itemsPerPage={pagination.per_page}
                onPageChange={onPageChange}
                variant="default"
                size="md"
              />
            </div>
          )}
        </>
      ) : (
        <div className="p-8 text-center">
          <FolderTree className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No categories found</h3>
          <p className="text-gray-600 mb-4">
            {searchTerm || parentFilter || statusFilter
              ? 'Try adjusting your search criteria or filters.'
              : 'Get started by creating your first category.'}
          </p>
          <button
            onClick={onCreateCategory}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <FolderTree className="h-4 w-4 mr-2" />
            Create Category
          </button>
        </div>
      )}
    </>
  );
};

export default CategoryTable;